import { Component, computed, effect, inject, input, model, output, signal, viewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AgGridAngular } from 'ag-grid-angular';
import { CellValueChangedEvent, ColDef, FilterModel, GridOptions, ITextFilterParams } from 'ag-grid-community';
import { PanModalComponent } from '../../../components/pan-modal/pan-modal.component';
import { APPLICATION_TIMEZONES } from '../../../constants';
import { tryGetErrorMessage } from '../../../error.utils';
import { defaultTableDefOptions, localStorageSignal } from '../../../helpers';
import { ArchiveService } from '../../../service/archive.service';
import { BranchService } from '../../../service/branch.service';
import { NotifyService } from '../../../service/notify.service';
import { UserLoginService } from '../../../service/user-login.service';
import { ArchivedLocation, Branch, LocationResponse } from '../../../types';
import { CellArchiveListActionsComponent } from './cell-archive-list-actions/cell-archive-list-actions.component';
import { CellLocationsListActionsComponent } from './cell-locations-list-actions/cell-locations-list-actions.component';
import { CellLocationsListHeaderComponent } from './cell-locations-list-header/cell-locations-list-header.component';

type TableLocation = LocationResponse & {
  toBeArchived: boolean;
  toBeTransferred: boolean;
};

@Component({
  selector: 'app-bl-management-locations',
  templateUrl: './bl-management-locations.component.html',
  styleUrls: ['./bl-management-locations.component.scss'],
  standalone: false,
  providers: [],
})
export class BlManagementLocationsComponent {
  private router = inject(Router);

  private userLoginService = inject(UserLoginService);
  private notifyService = inject(NotifyService);
  private branchService = inject(BranchService);
  private archiveService = inject(ArchiveService);

  public branchId = input.required<number>();
  public branches = input.required<Branch[]>();
  public isLoading = input<boolean>(false);
  public locations = model.required<LocationResponse[]>();
  public archivedLocations = model.required<ArchivedLocation[]>();
  public reloadLocations = output<void>();
  public selectLocation = output<LocationResponse>();
  public removeLocation = output<LocationResponse>();

  public view = model<'Active' | 'Archived'>('Active');

  public addModal = viewChild<PanModalComponent>('addModal');

  public mergeModal = viewChild<PanModalComponent>('mergeModal');
  public mergeFromLocation = signal<LocationResponse | undefined>(undefined);
  public mergeModalLocation = signal<LocationResponse | undefined>(undefined);

  public transferModal = viewChild<PanModalComponent>('transferModal');
  public transferLocation = signal<LocationResponse | undefined>(undefined);
  public transferToBranch = signal<Branch | undefined>(undefined);

  public grid = viewChild<AgGridAngular>('locationsGrid');
  private gridReady = signal(false);

  public uiLocationNameFilter = localStorageSignal<FilterModel>(
    {
      filterType: 'text',
      type: 'contains',
      filter: '',
    },
    'bl-management-locations-ui-location-name-filter',
  );

  public tableLocations = computed(() =>
    this.locations().map((l) => ({
      ...l,
      toBeArchived: !!l.keystore?.pending_actions?.find((x) => x.type === 'archive'),
      toBeTransferred: !!l.keystore?.pending_actions?.find((x) => x.type === 'transfer'),
    })),
  );

  public mergeableLocations = computed(() => this.locations().filter((l) => l.id !== this.mergeFromLocation()?.id));
  public transferrableBranches = computed(() => this.branches().filter((b) => b.id !== this.branchId()));

  public newLocationName = signal<string>('');
  public newLocationTimezone = signal<string | undefined>(undefined);
  public isNewLocationNationalAccount = signal<boolean>(false);
  public readonly allTimezones = signal<string[]>(APPLICATION_TIMEZONES);

  public readonly locationTableDef: GridOptions = {
    ...defaultTableDefOptions(),
    singleClickEdit: true,
    pagination: true,
    paginationPageSize: 50,
    onGridReady: (event) => {
      event.api.applyColumnState({
        state: [
          { colId: 'toBeArchived', sort: 'desc', sortIndex: 0 },
          { colId: 'toBeTransferred', sort: 'desc', sortIndex: 1 },
          { colId: 'name', sort: 'asc', sortIndex: 2 },
        ],
        defaultState: { sort: null },
      });

      event.api.setFilterModel({
        name: { filterType: 'text', type: 'contains', ...this.uiLocationNameFilter() },
      });

      this.gridReady.set(true);
    },
    onStateUpdated: (event) => {
      this.uiLocationNameFilter.set(event.api.getFilterModel().name ?? {});
    },
  };

  public readonly locationColumns: ColDef<TableLocation>[] = [
    {
      field: 'external_id',
      headerName: 'External ID',
      cellDataType: 'text',
      filter: 'agTextColumnFilter',
      sortable: true,
    },
    {
      field: 'id',
      headerName: 'Location ID',
      cellDataType: 'text',
      filter: 'agTextColumnFilter',
      sortable: true,
    },
    {
      field: 'name',
      headerName: 'Location Name',
      cellDataType: 'text',
      filter: 'agTextColumnFilter',
      sortable: true,
      tooltipField: 'name',
      filterParams: {
        buttons: ['reset'],
      } as ITextFilterParams,
    },
    {
      field: 'attributes.is_national_account',
      headerName: 'National Account?',
      cellDataType: 'boolean',
      editable: true,
      cellRenderer: 'agCheckboxCellRenderer',
      cellEditor: 'agCheckboxCellEditor',
      sortable: true,
      filter: 'agSetColumnFilter',
      valueGetter: (params) => params.data?.attributes.is_national_account ?? false,
      onCellValueChanged: (event: CellValueChangedEvent<TableLocation>) => this.updateNationalAccountAttribute(event),
    },
    {
      field: 'toBeArchived',
      headerName: 'To Be Archived',
      cellDataType: 'boolean',
      filter: 'agSetColumnFilter',
      sortable: true,
    },
    {
      field: 'toBeTransferred',
      headerName: 'To Be Transferred',
      cellDataType: 'text',
      filter: 'agTextColumnFilter',
      sortable: true,
      valueGetter: (data) => data.data?.keystore?.pending_actions?.find((x) => x.type === 'transfer')?.data?.to_branch_name,
    },
    {
      headerName: '',
      suppressColumnsToolPanel: true,
      suppressKeyboardEvent: () => true,
      sortable: false,
      width: 210,
      maxWidth: 210,
      cellClass: ['inline-flex', 'items-center', 'justify-end', 'gap-2'],
      cellRenderer: CellLocationsListActionsComponent,
      cellRendererParams: {
        editCallback: (data: LocationResponse) => {
          this.selectLocation.emit(data);
        },
        mergeCallback: (data: LocationResponse) => {
          this.mergeFromLocation.set(data);
          this.mergeModal()?.show();
        },
        transferCallback: (data: LocationResponse) => {
          this.transferLocation.set(data);
          this.transferModal()?.show();
        },
        archiveCallback: (data: LocationResponse) => {
          this.archiveService.archiveLocation(data.id).subscribe(() => {
            this.notifyService.notify(
              'Location archived successfully! It may take a few minutes to appear in the archived locations list.',
            );
            this.reloadLocations.emit();
          });
        },
        refreshCallback: () => {
          this.reloadLocations.emit();
        },
      },
      headerComponent: CellLocationsListHeaderComponent,
      headerClass: ['w-full', 'inline-flex', 'items-center', 'justify-end'],
      headerComponentParams: {
        addCallback: () => {
          this.addModal()?.show();
        },
      },
    },
  ];

  public readonly archiveTableDef: GridOptions = {
    ...defaultTableDefOptions(),
    pagination: true,
    paginationPageSize: 50,
  };

  public readonly archiveColumns: ColDef<ArchivedLocation>[] = [
    {
      field: 'id',
      headerName: 'Location ID',
      cellDataType: 'text',
      filter: 'agTextColumnFilter',
      sortable: true,
    },
    {
      field: 'name',
      headerName: 'Location Name',
      cellDataType: 'text',
      filter: 'agTextColumnFilter',
      sortable: true,
    },
    {
      headerName: '',
      suppressColumnsToolPanel: true,
      suppressKeyboardEvent: () => true,
      sortable: false,
      width: 210,
      maxWidth: 210,
      cellClass: ['inline-flex', 'items-center', 'justify-end', 'gap-2'],
      cellRenderer: CellArchiveListActionsComponent,
      cellRendererParams: {
        unarchiveCallback: (data: ArchivedLocation) => {
          this.archiveService.unarchiveLocation(data.id).subscribe(() => {
            this.notifyService.notify(
              'Location unarchived successfully! It may take a few minutes to appear in the active locations list.',
            );
            this.reloadLocations.emit();
          });
        },
      },
    },
  ];

  constructor() {
    effect(() => {
      if (!this.gridReady() || !this.grid()?.api) return;
      const isAdmin = this.userLoginService.currentUserIsAdmin();
      const isBranchOwner = this.userLoginService.currentUserHasRole('branch-owner');
      this.grid()?.api.setColumnsVisible(['toBeArchived', 'toBeTransferred'], isAdmin || isBranchOwner);
    });
  }

  createNewLocation(): void {
    const name = this.newLocationName();
    const timezone = this.newLocationTimezone()!;
    const is_national_account = this.isNewLocationNationalAccount();

    this.branchService.createBranchLocation(this.branchId(), { name, timezone, is_national_account }).subscribe({
      next: (location) => {
        this.router.navigate(['/home', 'manage', 'branch', this.branchId(), 'location', location.id]);
      },
      error: (error) => this.notifyService.notify(tryGetErrorMessage(error)),
    });
  }

  private updateNationalAccountAttribute(event: CellValueChangedEvent<TableLocation>) {
    this.branchService.updateBranchLocationAttribute(this.branchId(), event.data.id, 'is_national_account', !!event.newValue).subscribe({
      next: () => this.notifyService.notify('National Account status updated successfully'),
      error: (error) => {
        event.node.setDataValue('attributes.is_national_account', event.oldValue);
        this.notifyService.notify(tryGetErrorMessage(error));
      },
    });
  }

  public doTransfer() {
    if (!this.transferToBranch() || !this.transferLocation()) return;

    this.branchService
      .moveLocationsToBranch(this.branchId(), {
        location_ids: [this.transferLocation()!.id],
        to_branch_id: this.transferToBranch()!.id,
      })
      .subscribe(() => {
        this.notifyService.notify('Location transferred successfully');
        this.removeLocation.emit(this.transferLocation()!);

        this.transferLocation.set(undefined);
        this.transferToBranch.set(undefined);
      });
  }

  public doMerge() {
    if (!this.mergeModalLocation() || !this.mergeFromLocation()) return;

    this.branchService
      .mergeLocations(this.branchId(), {
        location_id: this.mergeFromLocation()!.id,
        into_location_id: this.mergeModalLocation()!.id,
      })
      .subscribe(() => {
        this.notifyService.notify('Locations merged successfully');
        this.removeLocation.emit(this.mergeFromLocation()!);

        this.mergeFromLocation.set(undefined);
        this.mergeModalLocation.set(undefined);
      });
  }
}
